import { useState } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { TrendUp } from "@phosphor-icons/react";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, formatSnakeCase, message } from "@/lib/utils";
import { BrandGovernanceApiFactory, CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import type { CreativesScorecardData, CreativesScorecardTrendItem } from "@/schema/brand-os/brand-governance";


const trendingTypes = ["brand", "sub_brand", "sub_brand_channel"] as const;
type TrendingType = (typeof trendingTypes)[number];

function formatTrendText(
  text: string,
  trendParams: {
    entity: string;
    metric: string;
    direction: string;
    pct_change: number | null;
    diff?: number | null;
  }
): string {
  const pct = trendParams.pct_change != null ? trendParams.pct_change.toFixed(1) : "—";
  const diffStr = trendParams.diff != null ? trendParams.diff.toFixed(2) : "—";
  return text
    .replaceAll("{entity}", trendParams.entity)
    .replaceAll("{metric}", trendParams.metric)
    .replaceAll("{direction}", trendParams.direction)
    .replaceAll("{pct_change}", pct)
    .replaceAll("{diff}", diffStr);
}

function TrendingComponent({ status, data }: { status: QueryStatus; data?: CreativesScorecardData }) {
  switch (status) {
    case "pending":
      return <TrendingSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <TrendingContent data={data} /> : <LockedContent message={message} />;
  }
}

export function Trending({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
          <TrendUp className="size-4 text-blue-600" />
        </div>
        <CardTitle>Trending This Week</CardTitle>
      </CardHeader>
      <CardContent>
        <TrendingComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function TrendingContent({ data }: { data: CreativesScorecardData }) {
  const { brand, sub_brand, sub_brand_channel } = data.trends;

  const defaultType: TrendingType = brand.length ? "brand" : sub_brand.length ? "sub_brand" : "sub_brand_channel"; 
  const [selectedType, setSelectedType] = useState<TrendingType>(defaultType);
  const selectedItems = selectedType === "brand" ? brand : selectedType === "sub_brand" ? sub_brand : sub_brand_channel;

  if (!brand.length && !sub_brand.length && !sub_brand_channel.length) {
    return <LockedContent message={message} />;
  }

  return (
    <div className="space-y-3">
      <Select value={selectedType} onValueChange={(value) => setSelectedType(value as TrendingType)}>
        <SelectTrigger className="min-w-48">
          <SelectValue placeholder={formatSnakeCase(selectedType)} />
        </SelectTrigger>
        <SelectContent>
          {trendingTypes
            .filter((type) => (type === "brand" ? brand.length : type === "sub_brand" ? sub_brand.length : sub_brand_channel.length) > 0)
            .map((type) => (
              <SelectItem key={type} value={type}>
                {formatSnakeCase(type)}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <ul className="max-h-72 overflow-y-auto pr-1">
        {selectedItems.map((item, index) => (
          <li key={`${item.params.entity}-${item.params.metric}-${index}`}>
            <TrendingRow item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrendingRow({ item }: { item: CreativesScorecardTrendItem }) {
  const { params } = item;
  const sentence = formatTrendText(item.text ?? "", params);
  const isPositive = params.direction === "improved";
  const pctLabel = params.pct_change != null ? `${isPositive ? "+" : "-"}${params.pct_change.toFixed(1)}%` : "—";

  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2 last:border-b-0">
      <p className="min-w-0 flex-1 break-words text-sm leading-snug text-foreground/90">{sentence}</p>
      <span className={cn("shrink-0 pt-0.5 text-xs font-semibold tabular-nums", isPositive ? "text-success" : "text-destructive")}>{pctLabel}</span>
    </div>
  );
}

function TrendingSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-9 w-full max-w-xs rounded-full" />
      <Skeleton className="h-24 w-full rounded-lg" />
    </div>
  );
}
